import React, { useMemo, useRef, useCallback, useEffect } from "react";
import {
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
} from "mdb-react-ui-kit";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { parseISO, format } from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const toNum = (v) => (typeof v === "number" ? v : Number(v) || 0);

/** Official Saudi Riyal mark (same paths as SARIcon). */
const SAR_VB = { w: 1124.14, h: 1256.39 };
const SAR_PATHS = [
  "M699.62,1113.02h0c-20.06,44.48-33.32,92.75-38.4,143.37l424.51-90.24c20.06-44.47,33.31-92.75,38.4-143.37l-424.51,90.24Z",
  "M1085.73,895.8c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.33v-135.2l292.27-62.11c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.27V66.13c-50.67,28.45-95.67,66.32-132.25,110.99v403.35l-132.25,28.11V0c-50.67,28.44-95.67,66.32-132.25,110.99v525.69l-295.91,62.88c-20.06,44.47-33.33,92.75-38.42,143.37l334.33-71.05v170.26l-358.3,76.14c-20.06,44.47-33.32,92.75-38.4,143.37l375.04-79.7c30.53-6.35,56.77-24.4,73.83-49.24l68.78-101.97v-.02c7.14-10.55,11.3-23.27,11.3-36.97v-149.98l132.25-28.11v270.4l424.53-90.28Z",
];

const SAR_SVG_INLINE = `<svg class="oh-monthly-tooltip__sar" viewBox="0 0 ${SAR_VB.w} ${SAR_VB.h}" aria-hidden="true" focusable="false"><path fill="#ffffff" d="${SAR_PATHS[0]}"/><path fill="#ffffff" d="${SAR_PATHS[1]}"/></svg>`;

const formatAxisAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (Math.abs(n) >= 1000) {
    const k = n / 1000;
    const label = Number.isInteger(k) ? `${k}` : k.toFixed(1);
    return `${label}K`;
  }
  return `${Math.round(n)}`;
};

const drawSarSymbol = (ctx, x, y, size, color) => {
  const scale = size / SAR_VB.w;
  const h = size * (SAR_VB.h / SAR_VB.w);
  ctx.save();
  ctx.translate(x, y - h / 2);
  ctx.scale(scale, scale);
  ctx.fillStyle = color;
  for (const d of SAR_PATHS) {
    ctx.fill(new Path2D(d));
  }
  ctx.restore();
};

/** Draw Riyal symbol to the left of each Y-axis tick label. */
const sarYAxisPlugin = {
  id: "sarYAxisSymbol",
  afterDraw(chart) {
    const yAxis = chart.scales?.y;
    if (!yAxis) return;
    const { ctx, chartArea } = chart;
    const tickOpts = yAxis.options?.ticks || {};
    const color = tickOpts.color || "#5E5E5E";
    const fontSize = tickOpts.font?.size || 12;
    const fontFamily = tickOpts.font?.family || "Inter";
    const fontWeight = tickOpts.font?.weight || "400";
    const tickPadding = tickOpts.padding ?? 8;
    const iconSize = 10;
    const gap = 3;

    ctx.save();
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    yAxis.ticks.forEach((tick) => {
      const label = formatAxisAmount(tick.value);
      const y = yAxis.getPixelForValue(tick.value);
      const textWidth = ctx.measureText(label).width;
      const labelRight = chartArea.left - tickPadding;
      const iconX = labelRight - textWidth - gap - iconSize;
      drawSarSymbol(ctx, iconX, y, iconSize, color);
    });
    ctx.restore();
  },
};

const MonthlySaleCard = ({ monthlySale }) => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const tooltipRef = useRef(null);

  useEffect(() => {
    return () => {
      if (tooltipRef.current?.parentNode) {
        tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      }
      tooltipRef.current = null;
    };
  }, []);

  const granularity = monthlySale?.granularity || null;
  const rawSales = Array.isArray(monthlySale?.data) ? monthlySale.data : [];

  const formatLabel = (d) => {
    if (!d) return "";
    if (granularity === "daily") {
      try {
        return format(parseISO(d), "MMM dd", isArabic ? { locale: arLocale } : undefined);
      } catch {
        return d;
      }
    }
    if (granularity === "monthly") {
      try {
        return format(
          parseISO(`${d}-01`),
          "MMM yyyy",
          isArabic ? { locale: arLocale } : undefined
        );
      } catch {
        return d;
      }
    }
    return d;
  };

  const labels = rawSales.map((item) => formatLabel(item?.date));
  const ordersSeries = rawSales.map((item) =>
    toNum(item?.totalOrders ?? item?.transactions)
  );
  const revenueSeries = rawSales.map((item) =>
    toNum(item?.totalRevenue ?? item?.sales)
  );

  const chartKey = useMemo(
    () =>
      rawSales
        .map((item) => `${item?.date}:${item?.sales ?? item?.totalRevenue}`)
        .join("|"),
    [rawSales]
  );

  // Figma uses singular "Transaction:" — avoid "Transactions:" wrapping in the bubble
  const transactionLabel = isArabic
    ? t("dashboardPage.transactions")
    : "Transaction";
  const salesLabel = t("dashboardPage.sales") || "Sales";

  const formatRevenue = useCallback(
    (n) =>
      toNum(n).toLocaleString(isArabic ? "ar-SA" : "en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [isArabic]
  );

  /** Figma 1:2869 — bubble above point, centered title, label/value rows, caret below */
  const externalTooltip = useCallback(
    (context) => {
      const { chart, tooltip } = context;
      const parent = chart.canvas.parentNode;
      if (!parent) return;

      let el = tooltipRef.current;
      if (!el || !parent.contains(el)) {
        el = document.createElement("div");
        el.className = "oh-monthly-tooltip";
        el.innerHTML = `
          <div class="oh-monthly-tooltip__card">
            <div class="oh-monthly-tooltip__title"></div>
            <div class="oh-monthly-tooltip__rows">
              <div class="oh-monthly-tooltip__row oh-monthly-tooltip__row--transaction">
                <span class="oh-monthly-tooltip__key"></span>
                <span class="oh-monthly-tooltip__val"></span>
              </div>
              <div class="oh-monthly-tooltip__row oh-monthly-tooltip__row--sales">
                <span class="oh-monthly-tooltip__key"></span>
                <span class="oh-monthly-tooltip__val"></span>
              </div>
            </div>
          </div>
          <div class="oh-monthly-tooltip__caret" aria-hidden="true"></div>
        `;
        parent.appendChild(el);
        tooltipRef.current = el;
      }

      if (tooltip.opacity === 0) {
        el.style.opacity = "0";
        el.style.pointerEvents = "none";
        return;
      }

      const dataIndex = tooltip.dataPoints?.[0]?.dataIndex;
      if (dataIndex == null) return;

      const titleEl = el.querySelector(".oh-monthly-tooltip__title");
      const rows = el.querySelectorAll(".oh-monthly-tooltip__row");
      const keys = el.querySelectorAll(".oh-monthly-tooltip__key");
      const vals = el.querySelectorAll(".oh-monthly-tooltip__val");

      if (rows[0]) {
        rows[0].classList.add("oh-monthly-tooltip__row--transaction");
      }
      if (rows[1]) {
        rows[1].classList.add("oh-monthly-tooltip__row--sales");
      }

      titleEl.textContent = labels[dataIndex] || "";
      keys[0].textContent = `${transactionLabel}:`;
      vals[0].innerHTML = `${SAR_SVG_INLINE}<span>${formatRevenue(revenueSeries[dataIndex])}</span>`;
      keys[1].textContent = `${salesLabel}:`;
      vals[1].textContent = String(Math.round(toNum(ordersSeries[dataIndex])));

      const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
      const caretX = tooltip.caretX;
      const caretY = tooltip.caretY;

      el.style.opacity = "1";
      el.style.pointerEvents = "none";
      el.style.left = `${positionX + caretX}px`;
      el.style.top = `${positionY + caretY}px`;
      el.setAttribute("data-rtl", isArabic ? "true" : "false");
    },
    [
      labels,
      revenueSeries,
      ordersSeries,
      transactionLabel,
      salesLabel,
      formatRevenue,
      isArabic,
    ]
  );

  const options = useMemo(
    () => {
      const m = Math.max(0, ...revenueSeries);
      const niceMax = (() => {
        if (!m) return 100;
        const padded = m * 1.15;
        // Prefer round K steps (0K / 50K / 100K / 150K) with Riyal symbol via plugin
        const k = padded / 1000;
        if (k <= 1) return Math.ceil(padded / 10) * 10 || 100;
        const stepK = Math.max(1, Math.ceil(k / 3));
        return stepK * 3 * 1000;
      })();
      const stepSize = niceMax / 3;

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        layout: {
          padding: {
            left: 8,
            right: 12,
            top: 12,
            bottom: 4,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
            external: externalTooltip,
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
              drawBorder: false,
            },
            ticks: {
              color: "#5E5E5E",
              font: { size: 12, family: "Inter", weight: "400" },
              maxRotation: 0,
              autoSkip: true,
              autoSkipPadding: 8,
            },
            border: {
              display: false,
            },
          },
          y: {
            min: 0,
            max: niceMax,
            // Extra width so the Riyal symbol isn’t clipped by the canvas edge
            afterFit(axis) {
              axis.width += 14;
            },
            ticks: {
              stepSize,
              color: "#5E5E5E",
              font: { size: 12, family: "Inter", weight: "400" },
              callback: formatAxisAmount,
              // Room for Riyal symbol + gap before the number
              padding: 8,
            },
            grid: {
              color: "#E8E8E8",
              drawTicks: false,
              drawBorder: false,
            },
            border: {
              display: false,
            },
          },
        },
      };
    },
    [revenueSeries, externalTooltip]
  );

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: "Revenue",
          data: revenueSeries,
          borderColor: "#FF9B53",
          backgroundColor: "rgba(255, 155, 83, 0.12)",
          tension: 0.35,
          pointBackgroundColor: "#FF9B53",
          pointBorderColor: "#FF9B53",
          pointBorderWidth: 0,
          // Figma ellipse points are 10×10
          pointRadius: 5,
          pointHoverRadius: 7,
          borderWidth: 3,
          fill: false,
        },
      ],
    }),
    [labels, revenueSeries]
  );

  return (
    <MDBCard
      className="oh-dashboard-card oh-dashboard-card--monthly h-100 shadow-0 border mb-0"
      dir={isArabic ? "rtl" : undefined}
    >
      <MDBCardBody className="oh-dashboard-monthly">
        <MDBCardTitle className="oh-dashboard-card__title oh-dashboard-monthly__title">
          {granularity === "daily"
            ? t("dashboardPage.dailyRevenue")
            : granularity === "weekly"
            ? t("dashboardPage.weeklyRevenue")
            : t("dashboardPage.monthlyRevenue")}
        </MDBCardTitle>

        <div className="oh-dashboard-card__chart oh-dashboard-card__chart--monthly">
          <Line
            key={chartKey}
            options={options}
            data={chartData}
            plugins={[sarYAxisPlugin]}
          />
        </div>
      </MDBCardBody>
    </MDBCard>
  );
};

export default MonthlySaleCard;
